import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BankIdLayout, RegistrationSteps } from '../components/BankIdLayout';
import { BankIdEntry } from '../components/LocalBankIdDebug';
import { useLocalBankIdDebug } from '../context/LocalBankIdDebugContext';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../services/api';
import { authenticatedHomePath } from '../utils/onboarding';
import { invitationErrorKey } from '../utils/invitationLink';
import type { BankIdAuthStart, InvitationDetails } from '../types';

export function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { establishBankIdSession } = useAuth();
  const debug = useLocalBankIdDebug();
  const invitationId = params.get('invitation') || undefined;
  const paramPersona = params.get('persona');
  const initialPersona = ['person', 'company', 'lawyer', 'inspector'].includes(paramPersona || '') ? paramPersona as BankIdAuthStart['personaType'] : 'person';
  const [persona, setPersona] = useState<BankIdAuthStart['personaType']>(initialPersona);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(Boolean(invitationId));
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    if (!invitationId) setPersona(initialPersona);
  }, [initialPersona, invitationId]);
  useEffect(() => {
    if (!invitationId) return;
    let disposed = false;
    void authApi.getInvitation(invitationId).then(data => {
      if (disposed) return;
      if (data.status === 'accepted') { navigate(`/login?invitation=${encodeURIComponent(invitationId)}`, { replace: true }); return; }
      if (data.linkState && data.linkState !== 'active') { setError(invitationErrorKey(data.linkState)); return; }
      setInvitation(data); setPersona(data.type === 'customer' ? 'person' : data.type);
    }).catch(() => { if (!disposed) setError('auth.register.invitationInvalid'); })
      .finally(() => { if (!disposed) setInvitationLoading(false); });
    return () => { disposed = true; };
  }, [invitationId, navigate]);
  return <BankIdLayout below={<Link className="d-inline-block py-2" to={invitationId ? `/login?invitation=${encodeURIComponent(invitationId)}` : '/login'}>{t('auth.register.loginLink')}</Link>}>
    <RegistrationSteps currentStep={0} />
    <h1 className="h3 text-center">{t('auth.bankid.registerTitle')}</h1>
    <p className="text-muted text-center">{t('auth.bankid.registerDescription')}</p>
    {!invitationId && (!paramPersona || debug.enabled) && <div className="mb-4">
      <label htmlFor="register-persona" className="form-label">{t('auth.register.accountTypeLabel')}</label>
      <select id="register-persona" className="form-select" value={persona} disabled={submitting} onChange={event => setPersona(event.target.value as BankIdAuthStart['personaType'])}>
        <option value="person">{t('auth.register.personOptionTitle')}</option>
        <option value="company">{t('auth.register.companyOptionTitle')}</option>
        {debug.enabled && ['lawyer', 'inspector'].map(value => <option key={value} value={value}>{t(`localBankIdDebug.personas.${value}`)}</option>)}
      </select>
    </div>}
    {invitation && <p className="alert alert-info">{t('auth.bankid.invitation')}</p>}
    {error ? <p className="alert alert-danger" role="alert">{t(error)}</p> : invitationLoading ? <p role="status">{t('common.loading')}</p> : <BankIdEntry request={{ purpose: 'register', personaType: persona, invitationId }} onBusyChange={setSubmitting} onContinue={async response => {
      if (invitationId) sessionStorage.setItem('pendingInvitation', invitationId);
      if (invitation) sessionStorage.setItem('onboardingContact', JSON.stringify({ email: invitation.contactEmail || invitation.customerEmail || '', phone: invitation.contactPhone || invitation.customerPhone || '' }));
      const user = await (response.user.syntheticTestActor || response.user.onboarding?.bankidAssuranceLevel === 'simulated' ? establishBankIdSession(response, 'tab') : establishBankIdSession(response));
      const destination = authenticatedHomePath(user);
      // The invitation link also confirms the invited email (ClickUp 869fmx898),
      // so an account still to verify it goes straight to the decision.
      navigate(invitationId && destination !== '/onboarding' ? `/invitations/${encodeURIComponent(invitationId)}/decision` : destination, { replace: true });
    }} />}
  </BankIdLayout>;
}
